import * as React                 from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import useRequest from '../../common/services/request';

import { useCommon } from '../common';

import InformationContext                                         from './context';
import type { InformationProviderProps }                          from './information.provider.props';
import type { TInformationTemplate, Promotion, IInformationItem } from './information.provider.types';
import informationReducer                                         from './reducer';

const InformationProvider: React.FC<InformationProviderProps> = function(props) {
  const location = useLocation();

  const {
          children,
          value,
        } = props;

  const { pages } = useCommon();
  const request   = useRequest();

  const match = React.useMemo(() => {
    return matchPath<Record<'page' | 'urlKey', string>>(location.pathname, {
      exact:  true,
      path:   '/:page?/:urlKey?',
      strict: false,
    });
  }, [location.pathname]);
  const page  = React.useMemo(() => {
    return (pages as any[]).find((page: any) => {
      return page.urlKey === match?.params.page && ['questions', 'terminology'].includes(page.template);
    });
  }, [pages, match?.params.page]);

  const [state, dispatch] = React.useReducer(informationReducer, {
    isInterior: /_interior$/.test(page?.template ?? ''),
    isLoaded:   !!value.items?.length,
    items:      value.items ?? [],
    loading:    false,
    template:   page?.template ?? 'terminology',
    type:       page?.template?.replace('_interior', '') ?? 'terminology',
  });

  const changeTab       = React.useCallback((page: TInformationTemplate) => {
    dispatch({
      payload: {
        page,
      },
      type:    'change-tab',
    });
  }, []);
  const getInitialState = React.useCallback((page: TInformationTemplate, section: string) => {
    dispatch({
      payload: {
        page,
      },
      type:    'get-initial-state_request',
    });

    return request.get(`/api/${section}`)
      .then((response: {
        items: {
          id: number;
          items: IInformationItem[];
          label: string;
        }[];
        page: TInformationTemplate;
        promotion: Promotion[];
      }) => {
        dispatch({
          payload: response,
          type:    'get-initial-state_success',
        });
      })
      .catch(() => {
        dispatch({ type: 'get-initial-state_failure' });
      });
  }, []);

  const context = React.useMemo(() => {
    return {
      ...state,
      changeTab,
      getInitialState,
    };
  }, [changeTab, getInitialState, state]);

  React.useEffect(() => {
    if (['questions', 'terminology'].includes(page?.template) && page.template !== state.template) {
      changeTab(page.template);
    }
  }, [page?.template]);

  return (
    <InformationContext.Provider value={context}>
      {children}
    </InformationContext.Provider>
  );
};

export default InformationProvider;